import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAsync } from '../../hooks/useAsync';
import MirrorLoader from '../ui/MirrorLoader';
import SearchableUserSelect from '../SearchableUserSelect';
import {
   ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
   Line, BarChart, Bar, LabelList, Cell
 } from "recharts";
import "./../../styles/Dashboard.css";
import Button from '../ui/Button';
import DashboardService from "../../services/DashboardService";
import userService from '../../services/UserService';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';

export default function Dashboard() {
  // 1) Compute the 3-month window (used by KPIs + Trends)
const startOfMonthUTC = (y, m) => new Date(Date.UTC(y, m, 1));
const endOfDayUTC = (d) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));

const [range] = useState(() => {
  const nowLocal = new Date();
  const to = endOfDayUTC(new Date(Date.UTC(
    nowLocal.getFullYear(), nowLocal.getMonth(), nowLocal.getDate()
  )));
  const from = startOfMonthUTC(nowLocal.getFullYear(), nowLocal.getMonth() - 2);
  return { from, to };
});


  // --- Analyst Report controls (ALL users) ---
  const [arFrom, setArFrom] = useState(() => {
  const d = new Date();
  d.setDate(1);                // jump to the 1st of this month
  d.setHours(0, 0, 0, 0);      // start of the day
  return d;
});
const [arTo, setArTo] = useState(() => {
  const d = new Date();
  d.setHours(23, 59, 59, 999); // now (end of today)
  return d;
});
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [analystsError, setAnalystsError] = useState(null);
  const [rawAllAnalysts, setRawAllAnalysts] = useState([]);
  const { userType } = useAuth();
  const { selectedSiteId } = useSite();
     


// -- users state --
const [users, setUsers] = useState([]);
const [usersLoading, setUsersLoading] = useState(false);
const [usersError, setUsersError] = useState(null);
const MIN_QUERY = 3;
// fetch users for dropdown; pass query explicitly
const fetchUsers = React.useCallback(async (query = "", showAll = false) => {
  try {
    setUsersLoading(true);
    setUsersError(null);

    if (!selectedSiteId) {
      setUsers([]);
      return;
    }
    // if not 'show all' and query too short, don't hit API
    if (!showAll && (!query || query.length < MIN_QUERY)) {

      return;
    }

    //const response = await userService.getUsers(showAll ? "" : query, selectedSite);
    const response = await userService.getAnalystsList(query);
    const raw = Array.isArray(response) ? response : [];
    const normalized = raw.map((u, idx) => ({
      id:
        u.id ??
        u.userId ??
        u.visorId ??
        idx,
      username:
        u.username ??
        u.userName ??
        u.name ??
        u.displayName ??
        u.email ??
        u.visorId ??
        "Unknown",
    }));
    setUsers(normalized);
  } catch (err) {
    console.error("Error fetching users:", err);
    setUsersError("Failed to load users");
    setUsers([]);
  } finally {
    setUsersLoading(false);
  }
}, [selectedSiteId]);

const handleUserQueryChange = React.useCallback((q) => {
   // Only hit server when 3+ chars
   if (q && q.length >= 3) {
     fetchUsers(q, false);
   }
}, [fetchUsers]);

  // --- PC-only Report controls ---
  const [pcFrom, setPcFrom] = useState(() => {
    const d = new Date();
     d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [pcTo, setPcTo] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [pcLoading, setPcLoading] = useState(false);
  const [pcError, setPcError] = useState(null);
  const [rawPcAnalysts, setRawPcAnalysts] = useState([]);

  // --- Single Analyst controls ---
  const [saUsername, setSaUsername] = useState("");
  const [saFrom, setSaFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [saTo, setSaTo] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState(null);
  const [singleAnalyst, setSingleAnalyst] = useState(null);

  // 2) KPIs
  const {
    data: rawProcessed,
    loading: procLoading,
    error: procError
  } = useAsync(() => DashboardService.getPendingReviewsReport(), []);
  const processed = rawProcessed || {};

  // 3) Daily review counts (Trends)
  const {
    data: rawDailyCounts,
    loading: dailyLoading,
    error: dailyError
  } = useAsync(
    () => DashboardService.getDailyReviewCounts(range.from, range.to),
    [range.from, range.to]
  );
  const dailyCounts = rawDailyCounts ?? [];

  // 4) Aggregate daily counts by month (for the line chart)
  const monthly = useMemo(() => {
    const buckets = {};
    dailyCounts.forEach(({ date, count }) => {
      const d = new Date(date);
      const key = d.toLocaleString("default", { year: "numeric", month: "short" });
      buckets[key] = (buckets[key] || 0) + Number(count || 0);
    });
    return Object.entries(buckets)
      .map(([date, time]) => ({ date, time }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [dailyCounts]);

  const VIBRANT = [
  "#ef4444", // red
  "#22c55e", // green
  "#3b82f6", // blue
  "#a855f7", // purple
  "#f59e0b", // amber
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#84cc16", // lime
  "#8b5cf6", // indigo
  "#f97316", // orange
];

  const getColor = (i) => VIBRANT[i % VIBRANT.length];
  // Helper to aggregate rows -> chart + table
  const aggregateAnalystRows = (rowsIn) => {
    const rows = Array.isArray(rowsIn) ? rowsIn : [];
    const byUser = new Map();
    const typeSet = new Set();

    rows.forEach((r) => {
      const Username = r.Username ?? r.username ?? "Unknown";
      const total = Number(r.TotalRecordsReviewed ?? r.totalRecordsReviewed ?? 0);
      const flagged = Number(r.TotalRecordsFlagged ?? r.totalRecordsFlagged ?? 0);
      const type = String(r.RecordType ?? r.recordType ?? "Unknown");

      if (!byUser.has(Username)) {
        byUser.set(Username, {
          Username,
          TotalRecordsReviewed: 0,
          TotalRecordsFlagged: 0,
          types: {}
        });
      }
      const u = byUser.get(Username);
      u.TotalRecordsReviewed += total;
      u.TotalRecordsFlagged += flagged;
      u.types[type] = (u.types[type] || 0) + total;
      typeSet.add(type);
    });

    const typeKeys = Array.from(typeSet).sort();

    const tableRows = Array.from(byUser.values())
      .map((u) => {
        const flat = { ...u };
        typeKeys.forEach((t) => (flat[t] = u.types[t] || 0));
        return flat;
      })
      .sort((a, b) => b.TotalRecordsReviewed - a.TotalRecordsReviewed);

    const barData = tableRows.slice(0, 10).map((u) => {
      const item = { Username: u.Username };
      typeKeys.forEach((t) => (item[t] = u[t] || 0));
      return item;
    });

    return { barData, typeKeys, tableRows };
  };

  // 5) All-analysts aggregates
  const { barData, typeKeys, tableRows } = useMemo(
    () => aggregateAnalystRows(rawAllAnalysts),
    [rawAllAnalysts]
  );

  // 6) PC-only aggregates
  const { barData: pcBar, typeKeys: pcTypeKeys, tableRows: pcTable } = useMemo(
    () => aggregateAnalystRows(rawPcAnalysts),
    [rawPcAnalysts]
  );

  // --- Helpers for date inputs ---
  const toDateInputValue = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const fromDateInputValue = (val, endOfDay = false) => {
    const [y, m, dd] = val.split("-").map(Number);
    const nd = new Date(y, (m || 1) - 1, dd || 1);
    if (endOfDay) nd.setHours(23, 59, 59, 999);
    else nd.setHours(0, 0, 0, 0);
    return nd;
  };

  // --- Generate handlers ---
  const handleGenerateAll = async () => {
    try {
      setAnalystsError(null);
      setAnalystsLoading(true);
      const data = await DashboardService.GetAllAnalystsReport(arFrom, arTo);
      setRawAllAnalysts(data || []);
    } catch (err) {
      setAnalystsError(err?.message || "Failed to load analyst report.");
      setRawAllAnalysts([]);
    } finally {
      setAnalystsLoading(false);
    }
  };

  const handleGeneratePc = async () => {
    try {
      setPcError(null);
      setPcLoading(true);
      const data = await DashboardService.GetAllPcRecordsReviewedByAllUsers(pcFrom, pcTo);
      setRawPcAnalysts(data || []);
    } catch (err) {
      setPcError(err?.message || "Failed to load PC-only report.");
      setRawPcAnalysts([]);
    } finally {
      setPcLoading(false);
    }
  };

  const handleGenerateSingle = async () => {
  if (!saUsername.trim()) {
    setSingleError("Please select a username.");
    setSingleAnalyst(null);
    return;
  }
    try {
      setSingleError(null);
      setSingleLoading(true);
      const data = await DashboardService.getAnalystReport(saUsername.trim(), saFrom,
      saTo);
      setSingleAnalyst(data || null);
    } catch (err) {
      setSingleError(err?.message || "Failed to load analyst data.");
      setSingleAnalyst(null);
    } finally {
      setSingleLoading(false);
    }
  };

  // Build a tiny bar dataset for the single analyst by record type (if provided)
  const singleBarData = useMemo(() => {
    if (!singleAnalyst) return [];
    if (Array.isArray(singleAnalyst)) {
      const byType = {};
      singleAnalyst.forEach((r) => {
        const t = String(r.RecordType ?? r.recordType ?? "Unknown");
        const v = Number(r.TotalRecordsReviewed ?? r.totalRecordsReviewed ?? 0);
        byType[t] = (byType[t] || 0) + v;
      });
      return Object.entries(byType).map(([t, v]) => ({ RecordType: t, Reviewed: v }));
    } else {
      const total = Number(singleAnalyst.TotalRecordsReviewed ?? singleAnalyst.totalRecordsReviewed ?? 0);
      const flagged = Number(singleAnalyst.TotalRecordsFlagged ?? singleAnalyst.totalRecordsFlagged ?? 0);
      return [{ RecordType: "Total", Reviewed: total, Flagged: flagged }];
    }
  }, [singleAnalyst]);

  return (
    userType === 'super' ? (
      <>
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h4>Review Overview</h4>
        <p>
          Monitor pending reviews and track your team's throughput.
          The cards below display review counts for Today, the Last 7 Days,
          the Last 14 Days and the Last 30 Days. The chart shows total records
          reviewed per month over the past three months.
        </p>
      </div>

      {/* KPI cards */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="dashboard-card-title">Pending Reviews</div>
          <div className="dashboard-card-value value-white">
            {procLoading ? <MirrorLoader width="100%" height="2.6rem" /> : (processed.lastOneDay ?? 0).toLocaleString()}
          </div>
          <div className="dashboard-card-title">Today</div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-title">Pending Reviews</div>
          <div className="dashboard-card-value value-green">
            {procLoading ? <MirrorLoader width="100%" height="2.6rem" /> : (processed.lastSevenDays ?? 0).toLocaleString()}
          </div>
          <div className="dashboard-card-title">Last 7 Days</div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-title">Pending Reviews</div>
          <div className="dashboard-card-value value-yellow">
            {procLoading ? <MirrorLoader width="100%" height="2.6rem" /> : (processed.lastFourteenDays ?? 0).toLocaleString()}
          </div>
          <div className="dashboard-card-title">Last 14 Days</div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card-title">Pending Reviews</div>
          <div className="dashboard-card-value value-red">
            {procLoading ? <MirrorLoader width="100%" height="2.6rem" /> : (processed.lastThirtyDays ?? 0).toLocaleString()}
          </div>
          <div className="dashboard-card-title">Last 30 Days</div>
        </div>
      </div>

      {/* Monthly trend line */}
      <div className="chart-card">
        <h2 className="dashboard-card-title">Trends</h2>
        {dailyLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : dailyError ? (
          <p className="text-danger"></p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="time" name="Reviewed" stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ALL Analysts Report */}
      <div className="chart-card">
        <div className="analyst-controls">
          <div>
            <h2 className="dashboard-card-title">Analyst Report (All Users)</h2>
            <p className="dashboard-subtext">Pick a date range and click Generate.</p>
          </div>
          <div className="analyst-controls-right">
            <label className="date-field">
              <span>From</span>
              <input type="date" value={toDateInputValue(arFrom)} onChange={(e) => setArFrom(fromDateInputValue(e.target.value, false))} max={toDateInputValue(arTo)} />
            </label>
            <label className="date-field">
              <span>To</span>
              <input type="date" value={toDateInputValue(arTo)} onChange={(e) => setArTo(fromDateInputValue(e.target.value, true))} min={toDateInputValue(arFrom)} />
            </label>
            <Button onClick={handleGenerateAll} disabled={analystsLoading} title="Generate Analyst Report (All Users)">
              {analystsLoading ? "Generating…" : "Generate"}
            </Button>

          </div>
        </div>

        {analystsLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : analystsError ? (
          <p className="text-danger">{String(analystsError)}</p>
        ) : barData.length === 0 ? (
          <p className="muted">No analyst data for this range. Try a different period.</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={barData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="Username" stroke="#6b7280" interval={0} />
              <YAxis stroke="#6b7280" allowDecimals={false} />
              <Tooltip />
              <Legend />
              {typeKeys.map((t, i) => (
                <Bar key={t} dataKey={t} stackId="totals" name={t} maxBarSize={48} fill={getColor(i)}>
                  <LabelList dataKey={t} position="top" formatter={(v) => (v ? v.toLocaleString() : "")} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ALL Analysts table */}
      <div className="table-card">
        <h2 className="dashboard-card-title">Analyst Breakdown (All Users)</h2>
        {analystsLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : analystsError ? (
          <p className="text-danger">{String(analystsError)}</p>
        ) : tableRows.length === 0 ? (
          <p className="muted">No rows to show.</p>
        ) : (
          <div className="table-responsive">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Analyst</th>
                  <th>Total Reviewed</th>
                  <th>Flagged</th>
                  {typeKeys.map((t) => (
                    <th key={`h-${t}`}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.Username}>
                    <td>{r.Username}</td>
                    <td>{(r.TotalRecordsReviewed || 0).toLocaleString()}</td>
                    <td>{(r.TotalRecordsFlagged || 0).toLocaleString()}</td>
                    {typeKeys.map((t) => (
                      <td key={`${r.Username}-${t}`}>{(r[t] || 0).toLocaleString()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PC-only Report */}
      <div className="chart-card">
        <div className="analyst-controls">
          <div>
            <h2 className="dashboard-card-title">PC Records (All Users)</h2>
            <p className="dashboard-subtext">Filter to PC-review activity only.</p>
          </div>
          <div className="analyst-controls-right">
            <label className="date-field">
              <span>From</span>
              <input type="date" value={toDateInputValue(pcFrom)} onChange={(e) => setPcFrom(fromDateInputValue(e.target.value, false))} max={toDateInputValue(pcTo)} />
            </label>
            <label className="date-field">
              <span>To</span>
              <input type="date" value={toDateInputValue(pcTo)} onChange={(e) => setPcTo(fromDateInputValue(e.target.value, true))} min={toDateInputValue(pcFrom)} />
            </label>
            <Button onClick={handleGeneratePc} disabled={pcLoading} title="Generate PC-only Report">
              {pcLoading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>

        {pcLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : pcError ? (
          <p className="text-danger">{String(pcError)}</p>
        ) : pcBar.length === 0 ? (
          <p className="muted">No PC records for this range.</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={pcBar} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="Username" stroke="#6b7280" interval={0} />
              <YAxis stroke="#6b7280" allowDecimals={false} />
              <Tooltip />
              <Legend />
              {pcTypeKeys.map((t, i) => (
  <Bar key={t} dataKey={t} stackId="totals" name={t} maxBarSize={48} fill={getColor(i)}>
    <LabelList dataKey={t} position="top" formatter={(v) => (v ? v.toLocaleString() : "")} />
  </Bar>
))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* PC-only table */}
      <div className="table-card">
        <h2 className="dashboard-card-title">PC Records Breakdown (All Users)</h2>
        {pcLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : pcError ? (
          <p className="text-danger">{String(pcError)}</p>
        ) : pcTable.length === 0 ? (
          <p className="muted">No rows to show.</p>
        ) : (
          <div className="table-responsive">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Analyst</th>
                  <th>Total Reviewed</th>
                  <th>Flagged</th>
                  {pcTypeKeys.map((t) => (
                    <th key={`pc-h-${t}`}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pcTable.map((r) => (
                  <tr key={`pc-${r.Username}`}>
                    <td>{r.Username}</td>
                    <td>{(r.TotalRecordsReviewed || 0).toLocaleString()}</td>
                    <td>{(r.TotalRecordsFlagged || 0).toLocaleString()}</td>
                    {pcTypeKeys.map((t) => (
                      <td key={`pc-${r.Username}-${t}`}>{(r[t] || 0).toLocaleString()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Single Analyst Report */}
      <div className="chart-card">
        <div className="analyst-controls">
          <div>
            <h2 className="dashboard-card-title">Analyst Report (Single User)</h2>
            <p className="dashboard-subtext">Enter a username and choose a date range, then Generate.</p>
          </div>
          <div className="analyst-controls-right">
<label className="select-field">
  <span>Username</span>
  <SearchableUserSelect
    value={saUsername}
    onSelect={setSaUsername}
    options={users}
    loading={usersLoading}
    onQueryChange={handleUserQueryChange}
    placeholder="Search users…"
  />
</label>
            <label className="date-field">
              <span>From</span>
              <input type="date" value={toDateInputValue(saFrom)} onChange={(e) => setSaFrom(fromDateInputValue(e.target.value, false))} max={toDateInputValue(saTo)} />
            </label>
            <label className="date-field">
              <span>To</span>
              <input type="date" value={toDateInputValue(saTo)} onChange={(e) => setSaTo(fromDateInputValue(e.target.value, true))} min={toDateInputValue(saFrom)} />
            </label>
            <Button onClick={handleGenerateSingle} disabled={singleLoading} title="Generate Single Analyst Report">
              {singleLoading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>

        {singleLoading ? (
          <MirrorLoader width="100%" height="2.6rem" />
        ) : singleError ? (
          <p className="text-danger">{String(singleError)}</p>
        ) : !singleAnalyst ? (
          <p className="muted">Enter a username and click Generate to view data.</p>
        ) : (
          <>
            <div className="single-summary">
              <div className="chip">
                Total Reviewed:{" "}
                <strong>
                  {(singleAnalyst.TotalRecordsReviewed ?? singleAnalyst.totalRecordsReviewed ?? 0).toLocaleString()}
                </strong>
              </div>
              {"TotalRecordsFlagged" in singleAnalyst || "totalRecordsFlagged" in singleAnalyst ? (
                <div className="chip">
                  Flagged:{" "}
                  <strong>
                    {(singleAnalyst.TotalRecordsFlagged ?? singleAnalyst.totalRecordsFlagged ?? 0).toLocaleString()}
                  </strong>
                </div>
              ) : null}
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={singleBarData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="RecordType" stroke="#6b7280" />
                <YAxis stroke="#6b7280" allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Reviewed" name="Reviewed">
     {singleBarData.map((_, idx) => (
       <Cell key={`r-${idx}`} fill={getColor(idx)} />
     ))}
   </Bar>
                {"Flagged" in (singleBarData[0] || {}) && (
  <Bar dataKey="Flagged" name="Flagged">
       {singleBarData.map((_, idx) => (
         <Cell key={`f-${idx}`} fill={getColor(idx + 5)} />
       ))}
     </Bar>
)}
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
    </>

  ) : null

  ); 

}
